import {
  SELLER_INVOICE_LOCALES,
  SELLER_INVOICE_MODE,
  SELLER_INVOICE_UPLOAD_MAX_BYTES,
  SELLER_INVOICE_UPLOAD_MIME,
  SellerInvoiceLocale,
  SellerInvoiceMode,
  VAT_REGIME,
  VatRegime,
} from "@/features/seller-invoice/static/seller-invoice.static";

export type SellerInvoiceMoney = { amount: number; currency: string };

/* -- ex-VAT line (D7) ------------------------------------------------------ */

export type VatPriceLine =
  | { kind: "exclVat"; price: SellerInvoiceMoney }
  | { kind: "marginNotDeductible" }
  | null;

/**
 * What goes under the price for this viewer. Only a business viewer sees
 * anything. The amount is always the gateway's `priceExclVat`: the web never
 * divides by a rate. A standard item without that amount shows nothing.
 */
export const getVatPriceLine = (input: {
  isBusinessViewer: boolean;
  vatRegime: VatRegime | null | undefined;
  priceExclVat: SellerInvoiceMoney | null | undefined;
}): VatPriceLine => {
  if (!input.isBusinessViewer) {
    return null;
  }

  if (input.vatRegime === VAT_REGIME.standard && input.priceExclVat) {
    return { kind: "exclVat", price: input.priceExclVat };
  }

  if (input.vatRegime === VAT_REGIME.margin) {
    return { kind: "marginNotDeductible" };
  }

  return null;
};

/* -- purchase Documents block (D9, D10) ------------------------------------ */

export type SellerInvoiceDocFields = {
  sellerInvoiceAvailable: boolean;
  sellerInvoiceNumber: string | null;
  whoppahInvoiceAvailable: boolean;
  sellerInvoiceUpload: { fileName: string; uploadedAt: string | null } | null;
  invoiceUploadAllowed: boolean;
  sellerInvoiceMode: SellerInvoiceMode | null;
  /** Seller side: the sales overview download is the Sales confirmation. */
  salesConfirmationAvailable: boolean;
  /** Seller side: Whoppah's invoice for the commission on this sale. */
  commissionInvoiceAvailable: boolean;
};

export type SellerInvoiceDocState = "generated" | "uploaded" | "pending";

/**
 * The buyer's "Factuur van de verkoper" row. An upload wins over a generated
 * invoice (the seller chose to provide his own); anything else is "volgt".
 */
export const getSellerInvoiceDocState = (
  fields: Pick<
    SellerInvoiceDocFields,
    "sellerInvoiceAvailable" | "sellerInvoiceUpload"
  >
): SellerInvoiceDocState => {
  if (fields.sellerInvoiceUpload) {
    return "uploaded";
  }

  if (fields.sellerInvoiceAvailable) {
    return "generated";
  }

  return "pending";
};

/** The rows of the Documents block, in order. Whoppah's only when the gateway has one. */
export const getPurchaseDocumentRows = (
  fields: SellerInvoiceDocFields | null
): Array<"orderSummary" | "sellerInvoice" | "whoppahInvoice"> => {
  if (!fields) {
    return ["orderSummary"];
  }

  return fields.whoppahInvoiceAvailable
    ? ["orderSummary", "sellerInvoice", "whoppahInvoice"]
    : ["orderSummary", "sellerInvoice"];
};

/** Seller side: the upload control shows in UPLOAD mode, and optionally for everyone allowed. */
export const showSellerInvoiceUpload = (
  fields: Pick<
    SellerInvoiceDocFields,
    "invoiceUploadAllowed" | "sellerInvoiceMode"
  >
): boolean =>
  fields.sellerInvoiceMode === SELLER_INVOICE_MODE.upload ||
  fields.invoiceUploadAllowed;

/* -- routes ---------------------------------------------------------------- */

export type SellerInvoiceRoute =
  | "seller-invoice"
  | "whoppah-invoice"
  | "commission-invoice"
  | "seller-invoice-upload";

/**
 * The order pk the existing receipt URLs carry (`/api/v1/order/invoice/<pk>/`):
 * the new routes sit next to it and take the same pk.
 */
export const getOrderPkFromReceiptUrl = (
  receiptUrl: string | null | undefined
): string | null => {
  if (!receiptUrl) {
    return null;
  }

  const match = /\/api\/v1\/order\/[^/]+\/([^/?#]+)\/?(?:[?#].*)?$/.exec(
    receiptUrl
  );

  return match?.[1] ?? null;
};

export const buildSellerInvoiceRouteUrl = (
  endpoint: string,
  route: SellerInvoiceRoute,
  orderPk: string
) =>
  `${endpoint.replace(/\/+$/, "")}/api/v1/order/${route}/${encodeURIComponent(
    orderPk
  )}/`;

/* -- upload validation (D9) ------------------------------------------------ */

export type InvoiceUploadProblem = "notPdf" | "tooLarge" | null;

const PDF_MAGIC = [0x25, 0x50, 0x44, 0x46]; // %PDF

/** Same checks the gateway makes, so the seller hears about them before the upload. */
export const validateInvoiceUpload = async (
  file: Pick<File, "size" | "type" | "name"> & {
    slice: (
      start: number,
      end: number
    ) => { arrayBuffer: () => Promise<ArrayBuffer> };
  }
): Promise<InvoiceUploadProblem> => {
  if (file.size > SELLER_INVOICE_UPLOAD_MAX_BYTES) {
    return "tooLarge";
  }

  const looksPdf =
    file.type === SELLER_INVOICE_UPLOAD_MIME ||
    file.name.toLowerCase().endsWith(".pdf");

  if (!looksPdf) {
    return "notPdf";
  }

  const head = new Uint8Array(await file.slice(0, 4).arrayBuffer());

  return PDF_MAGIC.every((byte, index) => head[index] === byte)
    ? null
    : "notPdf";
};

/* -- copy ------------------------------------------------------------------ */

export const toSellerInvoiceLocale = (
  language: string | null | undefined
): SellerInvoiceLocale => {
  const short = (language ?? "en").slice(0, 2).toLowerCase();

  return (SELLER_INVOICE_LOCALES as readonly string[]).includes(short)
    ? (short as SellerInvoiceLocale)
    : "en";
};
